import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import InvoiceTable from './components/InvoiceTable';
import InvoiceDetail from './components/InvoiceDetail';
import PDFViewer from './components/PDFViewer';
import StatsBar from './components/StatsBar';
import AdminPanel from './components/AdminPanel';
import './App.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

function App() {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAdmin, setShowAdmin] = useState(false);
  
  // Filter and pagination state
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sortBy: 'invoice_date',
    sortOrder: 'desc',
    limit: 50,
    offset: 0
  });

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      params.append('sort_by', filters.sortBy);
      params.append('sort_order', filters.sortOrder);
      params.append('limit', filters.limit);
      params.append('offset', filters.offset);

      const response = await axios.get(`${API_BASE}/api/invoices?${params}`);
      setInvoices(response.data.invoices);
      setTotalCount(response.data.total);
      
      // Reset selection when filters change
      if (filters.offset === 0 && response.data.invoices.length > 0) {
        setSelectedInvoice(response.data.invoices[0]);
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Navigate to next/previous invoice
  const navigateInvoice = (direction) => {
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < invoices.length) {
      setCurrentIndex(newIndex);
      setSelectedInvoice(invoices[newIndex]);
    } else if (newIndex >= invoices.length && newIndex < totalCount) {
      // Load next page
      setFilters(prev => ({
        ...prev,
        offset: prev.offset + prev.limit
      }));
    } else if (newIndex < 0 && filters.offset > 0) {
      // Load previous page
      setFilters(prev => ({
        ...prev,
        offset: Math.max(0, prev.offset - prev.limit)
      }));
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'n') {
        navigateInvoice(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'p') {
        navigateInvoice(-1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, invoices]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <div className="app">
      <header className="app-header">
        <h1>Invoice OCR Viewer</h1>
        <div className="header-actions">
          <button 
            className={`header-btn ${!showAdmin ? 'active' : ''}`}
            onClick={() => setShowAdmin(false)}
          >
            Invoices
          </button>
          <button 
            className={`header-btn ${showAdmin ? 'active' : ''}`}
            onClick={() => setShowAdmin(true)}
          >
            Admin
          </button>
        </div>
        {stats && <StatsBar stats={stats} />}
      </header>
      
      <div className="app-body">
        {showAdmin ? (
          <AdminPanel onRefresh={fetchStats} />
        ) : (
          <>
            <div className="left-panel">
              <InvoiceTable
                invoices={invoices}
                selectedInvoice={selectedInvoice}
                onSelectInvoice={(invoice, index) => {
                  setSelectedInvoice(invoice);
                  setCurrentIndex(index);
                }}
                filters={filters}
                onFilterChange={setFilters}
                totalCount={totalCount}
                loading={loading}
              />
            </div>
            
            <div className="middle-panel">
              {selectedInvoice && (
                <InvoiceDetail
                  invoiceId={selectedInvoice.id}
                  onNavigate={navigateInvoice}
                  currentIndex={currentIndex + filters.offset}
                  totalCount={totalCount}
                />
              )}
            </div>
            
            <div className="right-panel">
              {selectedInvoice && (
                <PDFViewer fileId={selectedInvoice.file_id} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default App;